import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import "./patientEducationPage.scss";
import EducationResource from "@assets/images/education_resource.png";
import Article from "@assets/svg/icons/article.svg";
import Close from "@assets/svg/icons/close.svg";
import DownArrow from "@assets/svg/icons/down-arrow.svg";
import Svg from "@natera/material/lib/svg";
import { ConfigContext } from "@app/provider";
import { ONCOLOGY_TEST_TYPES, TestType } from "@app/provider/testData/types";
import { TestDetailsRecord } from "@app/provider/types";
import TestCardService from "@app/service/testCard";
import { classifyCounselingIntent } from "@app/service/intakeForm";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import { useFaqData } from "./patientEducationFaq";
import { useIntakeForm, useGisGuest } from "./context";
import { useBookingConfirmedRedirect } from "./useBookingConfirmedRedirect";
import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import AlteraGuidePDF from "@assets/pdf/ALT_OS_PatientResultsGuide_Mobile_20240724_NAT-8021619.pdf";

/**
 * Allowed domains for external education resource links.
 * Only URLs matching these domains will be rendered as clickable links.
 */
const ALLOWED_LINK_DOMAINS = ["natera.com", "nateraspanish.as.me"];

/**
 * Validates that a URL is safe to render as an href.
 * Accepts HTTPS URLs on allowed domains, or relative paths (internal routes).
 */
const isValidResourceUrl = (url: string): boolean => {
  // Allow relative paths (internal routes like /result/...)
  if (url.startsWith("/")) {
    return true;
  }
  // Allow webpack-imported assets (data URIs or hashed asset paths)
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return true;
  }
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") {
      return false;
    }
    return ALLOWED_LINK_DOMAINS.some(
      (domain) =>
        parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
};

interface EducationResourceItem {
  title: string;
  link: string;
  /** Render as an in-app navigation instead of opening a new tab. */
  sameTab?: boolean;
}

const messages = defineMessages({
  thankYouTitle: {
    id: "patientEducation.thankYouTitle",
    defaultMessage: "Thank you for submitting the additional information",
  },
  faqDescription: {
    id: "patientEducation.faqDescription",
    defaultMessage:
      "We have a list of Frequently Asked Questions that might help you.",
  },
  resourcesHelpfulInfo: {
    id: "patientEducation.resourcesHelpfulInfo",
    defaultMessage:
      "Based on the information you provided, we've found a few resources that can be helpful for you to review.",
  },
  resourcesTitle: {
    id: "patientEducation.resourcesTitle",
    defaultMessage: "Resources for you",
  },
  articleType: {
    id: "patientEducation.articleType",
    defaultMessage: "Article",
  },
  continueToSchedule: {
    id: "patientEducation.continueToSchedule",
    defaultMessage: "Continue to schedule",
  },
  importantNote: {
    id: "patientEducation.importantNote",
    defaultMessage: "Important note",
  },
  counselorLimitations: {
    id: "patientEducation.counselorLimitations",
    defaultMessage:
      "Our genetic counselors won't be able to provide additional details beyond the shared materials, medical advice or specific treatment recommendations.",
  },
  contactProvider: {
    id: "patientEducation.contactProvider",
    defaultMessage:
      "For personal care decisions, please contact your healthcare provider. If you'd still like to talk with a counselor, please continue to schedule a session.",
  },
  confirm: {
    id: "patientEducation.confirm",
    defaultMessage: "Confirm",
  },
  cancel: {
    id: "patientEducation.cancel",
    defaultMessage: "Cancel",
  },
  loadError: {
    id: "patientEducation.loadError",
    defaultMessage: "Unable to load test information. Please try again later.",
  },
  backToHome: {
    id: "patientEducation.backToHome",
    defaultMessage: "Back to home",
  },
});

export default function PatientEducationPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  // No-order patients (public GIS funnel) have no backing test card, so the
  // test-card query is skipped and they're always presented the Signatera
  // (ONC) education experience.
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  // Anonymous order-lookup guest. Guests have no authenticated test card, so
  // the test-card query is skipped and the matched order's test type / status
  // (carried on the guest context) stand in for it.
  const {
    token: guestToken,
    testType: guestTestType,
    status: guestStatus,
    counselingIntent: guestCounselingIntent,
  } = useGisGuest();
  const isGuest = Boolean(guestToken);

  const { counselingIntent, setCounselingIntent, prefilledData } =
    useIntakeForm();

  const { data, loading, error } = useQuery<{
    getTestCardDetails: TestDetailsRecord;
  }>(TestCardService.getTestCardDetails(), {
    variables: { orderUidPublic, testUid },
    fetchPolicy: "cache-and-network",
    skip: !orderUidPublic || !testUid || isNoOrder || isGuest,
  });
  const latestTest = data?.getTestCardDetails ?? null;

  // Status used for counseling-intent classification, sourced from the guest
  // context for guests and the test card otherwise.
  const effectiveStatus = isGuest
    ? (guestStatus ?? undefined)
    : latestTest?.status;

  // Drives FAQs/resources. No-order patients (authenticated or guest) have no
  // test card of their own, so use the test they picked at the select-test step
  // (persisted on `prefilledData.testType`), falling back to Signatera when the
  // selection is missing. Order-backed guests use the matched order's test type.
  const effectiveTestType = isNoOrder
    ? ((prefilledData?.testType as TestType | undefined) ?? TestType.SIGNATERA)
    : isGuest
      ? (guestTestType as TestType | undefined)
      : latestTest?.testType;
  const [showPopup, setShowPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedFaqs, setExpandedFaqs] = useState<number[]>([2]);
  const faqData = useFaqData();

  // Once the appointment is booked the intake answers are locked; bounce the
  // patient out of this editable step so they can't navigate back and edit.
  const bookingConfirmedRedirect = useBookingConfirmedRedirect();

  // Prefer the intent classified at intake entry; otherwise derive it here so
  // direct entries to the education page still use the canonical rule
  // (RESULT_READY and TEST_COMPLETE → post-test, else pre-test). A guest's
  // status is a raw SOS sales status that rule can't read (a released
  // Signatera result reports TNP), so use the intent the server resolved.
  const effectiveIntent =
    counselingIntent ??
    guestCounselingIntent ??
    classifyCounselingIntent(effectiveStatus);
  const isPreTest = effectiveIntent === "pre-test";

  // Backfill the intake context if we landed here without one (e.g. deep link).
  // Safe to write effectiveIntent because the guard ensures we only run when
  // counselingIntent is null, in which case it equals the locally-derived value.
  useEffect(() => {
    if (counselingIntent || (!effectiveStatus && !guestCounselingIntent)) {
      return;
    }
    setCounselingIntent(effectiveIntent);
  }, [
    counselingIntent,
    guestCounselingIntent,
    effectiveIntent,
    effectiveStatus,
    setCounselingIntent,
  ]);
  const isSignatera = effectiveTestType === TestType.SIGNATERA;
  const faqs = useMemo(() => {
    if (!effectiveTestType) return [];
    const testData = faqData[effectiveTestType];
    if (!testData) return [];
    return isPreTest ? testData.preTest : testData.postTest || [];
  }, [effectiveTestType, isPreTest, faqData]);

  const resources = useMemo(() => {
    const resourcesList: EducationResourceItem[] = [];
    if (!effectiveTestType) return resourcesList;

    const understandingYourResultsTitle = intl.formatMessage({
      id: "understandingYourResultsHeadline",
      defaultMessage: "Understanding Your Results",
    });

    if (ONCOLOGY_TEST_TYPES.includes(effectiveTestType as TestType)) {
      if (latestTest?.latestResultDocumentUid) {
        resourcesList.push({
          title: understandingYourResultsTitle,
          link: routes.understandingYourResultsPage(
            latestTest.orderUidPublic,
            latestTest.uid,
            latestTest.status,
            latestTest.latestResultDocumentUid
          ),
        });
      } else if (isGuest && !isNoOrder && !isPreTest) {
        // Guests have no test card to link a result document from, so point
        // them at the guest-safe explainer, which carries the same
        // test-type-keyed content without the report itself. It's offered only
        // to guests whose order was found and is post-test: without an order
        // there are no results of theirs to understand.
        resourcesList.push({
          title: understandingYourResultsTitle,
          link: routes.scheduleAGeneticInformationSessionUnderstandingResultsPage(
            orderUidPublic ?? "",
            testUid ?? ""
          ),
          // A new tab starts with an empty sessionStorage, which is where the
          // guest session lives, so this one has to stay in place.
          sameTab: true,
        });
      }

      if (effectiveTestType === TestType.ALTERA) {
        resourcesList.push({
          title: intl.formatMessage({
            id: "downloadableAlteraResultsGuide",
            defaultMessage: "Downloadable Altera Results Guide",
          }),
          link: AlteraGuidePDF,
        });
      }

      const educationKey =
        effectiveTestType as keyof typeof config.links.EDUCATION;
      const educationLinks = config.links.EDUCATION[educationKey];

      if (educationLinks === undefined) {
        console.warn(
          `No education links configured for test type: ${effectiveTestType}`
        );
      }

      resourcesList.push(...(educationLinks || []));
    }
    return resourcesList;
  }, [
    effectiveTestType,
    latestTest,
    config.links.EDUCATION,
    intl,
    isGuest,
    isNoOrder,
    isPreTest,
    orderUidPublic,
    testUid,
  ]);

  const handleContinue = () => {
    setShowPopup(true);
  };

  const handleConfirm = () => {
    setIsSaving(true);
    setShowPopup(false);
    navigate(
      routes.scheduleAGeneticInformationSessionSchedulePage(
        orderUidPublic!,
        testUid!
      )
    );
  };

  const handleCancel = () => {
    setShowPopup(false);
  };

  const toggleFaq = (index: number) => {
    setExpandedFaqs((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (bookingConfirmedRedirect) {
    return bookingConfirmedRedirect;
  }

  if (!config.testSelfEducation.enabled) {
    return <Navigate to={routes.home} replace />;
  }

  if (!isNoOrder && !isGuest && loading && !latestTest) {
    return (
      <div className="patient-education-page">
        <div className="patient-education-page__card">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  if (!isNoOrder && !isGuest && (error || !latestTest)) {
    return (
      <div className="patient-education-page">
        <div className="patient-education-page__card">
          <p>{intl.formatMessage(messages.loadError)}</p>
          <button
            className="patient-education-page__button"
            onClick={() => navigate(routes.home)}
          >
            {intl.formatMessage(messages.backToHome)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-education-page">
      <div className="patient-education-page__card">
        <div className="patient-education-page__header-section">
          <h1 className="patient-education-page__title">
            {intl.formatMessage(messages.thankYouTitle)}
          </h1>
          {faqs.length > 0 && (
            <h3 className="patient-education-page__description">
              {intl.formatMessage(messages.faqDescription)}
            </h3>
          )}
        </div>

        <div className="patient-education-page__faq-section">
          {faqs.map((faq, index) => {
            const isExpanded = expandedFaqs.includes(index);
            return (
              <div key={index} className="patient-education-page__faq-item">
                <button
                  className="patient-education-page__faq-header"
                  onClick={() => toggleFaq(index)}
                  type="button"
                >
                  <p className="patient-education-page__faq-header-title">
                    {faq.title}
                  </p>
                  <div
                    className={`patient-education-page__faq-header-icon${
                      isExpanded
                        ? " patient-education-page__faq-header-icon--expanded"
                        : ""
                    }`}
                  >
                    <Svg content={DownArrow} />
                  </div>
                </button>
                {isExpanded && faq.content.length > 0 && (
                  <div className="patient-education-page__faq-content">
                    {faq.content.map((qa, i) => (
                      <div
                        key={i}
                        className="patient-education-page__faq-content-qa"
                      >
                        <p className="patient-education-page__faq-content-question">
                          {qa.question}
                        </p>
                        <p className="patient-education-page__faq-content-answer">
                          {qa.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {resources.length > 0 && (
          <div className="patient-education-page__resources-wrapper">
            <p className="patient-education-page__additional-info">
              {intl.formatMessage(messages.resourcesHelpfulInfo)}
            </p>
            <div className="patient-education-page__resources-section">
              <h2 className="patient-education-page__resources-title">
                {intl.formatMessage(messages.resourcesTitle)}
              </h2>
              <div
                className={`patient-education-page__list${
                  (faqs?.length ?? 0) === 0
                    ? " patient-education-page__list--vertical"
                    : ""
                }`}
              >
                {resources.map((resource, index) => {
                  const href =
                    typeof resource.link === "string" &&
                    isValidResourceUrl(resource.link)
                      ? resource.link
                      : "#";
                  const content = (
                    <>
                      <img className="media" src={EducationResource} alt="" />
                      <div className="patient-education-page__item-content">
                        <div className="patient-education-page__item-type">
                          <Svg content={Article} />
                          {intl.formatMessage(messages.articleType)}
                        </div>
                        <p className="patient-education-page__item-title">
                          {resource.title}
                        </p>
                      </div>
                    </>
                  );
                  return (
                    <React.Fragment key={index}>
                      {resource.sameTab ? (
                        <Link
                          className="patient-education-page__item"
                          to={href}
                        >
                          {content}
                        </Link>
                      ) : (
                        <a
                          className="patient-education-page__item"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {content}
                        </a>
                      )}
                      {index < resources.length - 1 && (
                        <div className="patient-education-page__divider" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <button
          className="patient-education-page__button"
          onClick={handleContinue}
        >
          {intl.formatMessage(messages.continueToSchedule)}
        </button>
      </div>
      {showPopup && (
        <div className="patient-education-page__popup-overlay">
          <div className="patient-education-page__popup">
            <div className="patient-education-page__popup-header">
              <h3 className="patient-education-page__popup-title">
                {intl.formatMessage(messages.importantNote)}
              </h3>
              <button
                className="patient-education-page__popup-close"
                onClick={handleCancel}
              >
                <Svg content={Close} />
              </button>
            </div>
            <div className="patient-education-page__popup-content">
              <p>{intl.formatMessage(messages.counselorLimitations)}</p>
              <p>{intl.formatMessage(messages.contactProvider)}</p>
            </div>
            <div className="patient-education-page__popup-buttons">
              <button
                className="patient-education-page__popup-confirm"
                onClick={handleConfirm}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="patient-education-page__popup-confirm-spinner" />
                ) : (
                  intl.formatMessage(messages.confirm)
                )}
              </button>
              <button
                className="patient-education-page__popup-cancel"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {intl.formatMessage(messages.cancel)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
